<template>
  <div data-cy="auth" class="auth" :class="$route.name">
    <div class="functional-section">
      <auth-tabs :selected="$route.name" />
      <div class="content">
        <router-view :currentPackage="currentPackage" :billingCycle="billingCycle" :quantity="quantity" />
      </div>
      <div class="logo"></div>
    </div>
    <div class="informational-section">
      <div class="information-wrapper">
        <Vue2React v-if="$route.name === 'signup'" :component="SignUpInfo" :props="signupProps" />
        <login-info v-else />
        <div class="mobile-logo"></div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthTabs from './components/partials/AuthTabs';
import LoginInfo from './components/partials/LoginInfo';
import Vue2React from '~/common/components/Vue2React.vue';
import { SignUpInfo } from '~/views/auth/sign-up-info';
import { isSerpPackage, isAiPackage, isTrialEligible, hasTrialInParams } from '~/views/auth/billing-utils';
import { aiVisibilityCredits } from '@/constants';
import { useQuery } from '@tanstack/vue-query';
import { getPackagesQuery } from '~/root/queries';
import { reactive } from '@vue/composition-api';

export default {
  name: 'Auth',
  components: {
    AuthTabs,
    LoginInfo,
    Vue2React,
  },
  data() {
    return {
      billingCycle: 'monthly',
      credits: aiVisibilityCredits[0].value,
      SignUpInfo,
    };
  },
  setup() {
    const packageIdentity = reactive({ value: undefined });
    const packagesQuery = useQuery({
      ...getPackagesQuery(),
      enabled: () => packageIdentity.value !== undefined,
    });
    return { packagesQuery, packageIdentity };
  },
  computed: {
    packages() {
      return this.packagesQuery.data.value || [];
    },
    currentPackage() {
      if (this.packageIdentity.value === null && this.packages.length) {
        this.packageIdentity.value = this.packages.find(
          (pkg) => isSerpPackage(pkg) && pkg.keywords_tracked >= 200,
        ).package_identity;
      }
      return this.packages.find((pkg) => pkg.package_identity === this.packageIdentity.value) || null;
    },
    trialExists() {
      return isTrialEligible(this.currentPackage) && hasTrialInParams(this.$route.query);
    },
    quantity() {
      return this.currentPackage && isAiPackage(this.currentPackage) ? this.credits : undefined;
    },
    signupProps() {
      return {
        packages: this.packages,
        currentPackage: this.currentPackage,
        onIdentityChange: (identity) => (this.packageIdentity.value = identity),
        billingCycle: this.billingCycle,
        onBillingCycleChange: (billingCycle) => (this.billingCycle = billingCycle),
        credits: this.credits,
        onCreditsChange: (credits) => (this.credits = credits),
        trialExists: this.trialExists,
      };
    },
  },
  async created() {
    if (this.$route.query.package_identity) {
      this.packageIdentity.value = this.$route.query.package_identity;
    }
    if (!this.packageIdentity.value) {
      this.packageIdentity.value = null;
    }
  },
};
</script>
<style lang="scss" scoped>
.auth {
  display: flex;
  width: 100%;
  margin: 0;
  padding: 0;

  ::v-deep .functional-section {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 39px;
    padding: 32px 48px;
    width: 100%;
    max-width: 596px;

    .content {
      display: flex;
      justify-content: center;
      width: 100%;

      & > div {
        width: 100%;
        max-height: 422px;
      }
    }

    .logo {
      background-image: url(~img/logo-full.svg);
      background-repeat: no-repeat;
      background-size: contain;
      width: 153px;
      height: 30px;
    }
  }

  .informational-section {
    width: 384px;
    background-image: url(~img/auth-info-frame-background.png);
    background-repeat: no-repeat;
    background-size: cover;

    .information-wrapper {
      font-family: Aeonik;
      display: flex;
      width: 384px;
      height: 100%;
      gap: 10px;
      color: white;
      background: linear-gradient(0deg, rgba(30, 44, 67, 0.8) 0%, rgba(30, 44, 67, 0.8) 100%);
      padding: 0px 17px 90px 17px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      align-self: stretch;

      .mobile-logo {
        display: none;
      }
    }
  }

  &.signup {
    ::v-deep .functional-section {
      gap: 24px;

      .content > div {
        max-height: 580px;
      }
    }

    .informational-section {
      .information-wrapper {
        align-items: flex-start;
        justify-content: start;
        padding: 45px 32px;
        background: linear-gradient(0deg, rgba(30, 44, 67, 0.9) 0%, rgba(30, 44, 67, 0.9) 100%);
      }
    }
  }

  @media (max-width: 980px) {
    ::v-deep .functional-section {
      max-width: 506px;
      padding: 32px 16px;

      .content > div {
        height: fit-content;
      }
    }

    .informational-section {
      display: none;
    }

    &.forgotPassword {
      .functional-section {
        gap: 39px;
      }
    }

    &.signup {
      display: flex;
      flex-direction: column;
      gap: 48px;

      .functional-section {
        padding-bottom: 0;
        .logo {
          display: none;
        }
      }

      .informational-section {
        display: initial;
        // display: none;
        background: none;
        width: 100%;

        .information-wrapper {
          background: none;
          padding: 0 16px 32px;
          width: 100%;

          ::v-deep .toggler-row .billing-label,
          ::v-deep .price-row,
          ::v-deep .permission {
            color: #15394f;
            font-family: Aeonik;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
          }

          .mobile-logo {
            display: block;
            background-image: url(~img/logo-full.svg);
            background-repeat: no-repeat;
            background-size: contain;
            width: 153px;
            height: 30px;
            margin: 48px auto;
          }
        }
      }
    }
  }
}
</style>
